const INDEX_CELL_SIZE = 16;
export function cornerAt(area, index) {
    const count = area.corners.length / 3;
    const offset = ((index % count) + count) % count;
    return {
        x: area.corners[offset * 3],
        y: area.corners[offset * 3 + 1],
        z: area.corners[offset * 3 + 2],
    };
}
function cross(origin, first, second) {
    return ((first.x - origin.x) * (second.y - origin.y) -
        (first.y - origin.y) * (second.x - origin.x));
}
/** Valve-Areas sind konvex; Vorzeichenwechsel heißt außerhalb. */
export function areaContains(area, point) {
    const count = area.corners.length / 3;
    let sign = 0;
    for (let index = 0; index < count; index += 1) {
        const value = cross(cornerAt(area, index), cornerAt(area, index + 1), point);
        if (Math.abs(value) < 1e-9)
            continue;
        const current = value > 0 ? 1 : -1;
        if (sign === 0)
            sign = current;
        else if (sign !== current)
            return false;
    }
    return true;
}
function closestOnSegment(a, b, point) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length2 = dx * dx + dy * dy;
    const t = length2 <= 1e-12
        ? 0
        : Math.min(1, Math.max(0, ((point.x - a.x) * dx + (point.y - a.y) * dy) / length2));
    return { x: a.x + dx * t, y: a.y + dy * t, z: a.z + (b.z - a.z) * t };
}
/** Höhe der Area-Fläche an einer Stelle (Dreiecksfächer, baryzentrisch). */
export function areaHeightAt(area, point) {
    const count = area.corners.length / 3;
    const origin = cornerAt(area, 0);
    for (let index = 1; index < count - 1; index += 1) {
        const b = cornerAt(area, index);
        const c = cornerAt(area, index + 1);
        const denominator = (b.y - c.y) * (origin.x - c.x) + (c.x - b.x) * (origin.y - c.y);
        if (Math.abs(denominator) < 1e-12)
            continue;
        const u = ((b.y - c.y) * (point.x - c.x) + (c.x - b.x) * (point.y - c.y)) /
            denominator;
        const v = ((c.y - origin.y) * (point.x - c.x) +
            (origin.x - c.x) * (point.y - c.y)) /
            denominator;
        const w = 1 - u - v;
        if (u >= -1e-6 && v >= -1e-6 && w >= -1e-6)
            return u * origin.z + v * b.z + w * c.z;
    }
    let sum = 0;
    for (let index = 0; index < count; index += 1)
        sum += cornerAt(area, index).z;
    return sum / count;
}
/** Nächster Punkt auf der Area (der Punkt selbst, wenn er innen liegt). */
export function closestPointInArea(area, point) {
    if (areaContains(area, point))
        return {
            x: point.x,
            y: point.y,
            z: areaHeightAt(area, point),
            distance: 0,
        };
    const count = area.corners.length / 3;
    let best = { x: point.x, y: point.y, z: 0, distance: Infinity };
    for (let index = 0; index < count; index += 1) {
        const candidate = closestOnSegment(cornerAt(area, index), cornerAt(area, index + 1), point);
        const distance = Math.hypot(candidate.x - point.x, candidate.y - point.y);
        if (distance < best.distance)
            best = { ...candidate, distance };
    }
    return best;
}
export function areaCentroid(area) {
    const count = area.corners.length / 3;
    let x = 0;
    let y = 0;
    for (let index = 0; index < count; index += 1) {
        x += area.corners[index * 3];
        y += area.corners[index * 3 + 1];
    }
    return { x: x / count, y: y / count };
}
export function areaSurface(area) {
    const count = area.corners.length / 3;
    let sum = 0;
    for (let index = 0; index < count; index += 1) {
        const a = cornerAt(area, index);
        const b = cornerAt(area, index + 1);
        sum += a.x * b.y - b.x * a.y;
    }
    return Math.abs(sum) / 2;
}
const areaIndexCache = new WeakMap();
const cellKey = (x, y) => x * 4096 + y;
function areaIndex(asset) {
    const cached = areaIndexCache.get(asset);
    if (cached)
        return cached;
    const cells = new Map();
    asset.areas.forEach((area, areaId) => {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (let index = 0; index < area.corners.length; index += 3) {
            minX = Math.min(minX, area.corners[index]);
            maxX = Math.max(maxX, area.corners[index]);
            minY = Math.min(minY, area.corners[index + 1]);
            maxY = Math.max(maxY, area.corners[index + 1]);
        }
        for (let x = Math.floor(minX / INDEX_CELL_SIZE); x <= Math.floor(maxX / INDEX_CELL_SIZE); x += 1)
            for (let y = Math.floor(minY / INDEX_CELL_SIZE); y <= Math.floor(maxY / INDEX_CELL_SIZE); y += 1) {
                const key = cellKey(x, y);
                const list = cells.get(key);
                if (list)
                    list.push(areaId);
                else
                    cells.set(key, [areaId]);
            }
    });
    const index = { cells };
    areaIndexCache.set(asset, index);
    return index;
}
/** Alle Areas, deren Bounding-Box den Kreis um `point` schneiden kann. */
export function areasNear(asset, point, radius) {
    const { cells } = areaIndex(asset);
    const found = new Set();
    for (let x = Math.floor((point.x - radius) / INDEX_CELL_SIZE); x <= Math.floor((point.x + radius) / INDEX_CELL_SIZE); x += 1)
        for (let y = Math.floor((point.y - radius) / INDEX_CELL_SIZE); y <= Math.floor((point.y + radius) / INDEX_CELL_SIZE); y += 1)
            for (const areaId of cells.get(cellKey(x, y)) ?? [])
                found.add(areaId);
    return [...found];
}
